import i18n from 'i18n-literally'

import { inflect } from 'inflection'

import config from '~/src/App/config'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import safeStorage from '~/src/Lib/safeStorage'
import {
  EMPTY_OBJECT,
  formattedDate,
  getDateTime,
  getDueDateDiff,
} from '~/src/Lib/Utils'

const storage = safeStorage()
const notProd = process.env.NODE_ENV !== 'production' || config.ENVIRONMENT !== 'production'
const debugEnabled = notProd && safeStorage('local').debug === 'true'
const logger = createLogger('Account/utils')

const testDueDate = getGlobal() && debugEnabled && storage.getItem('testDueDate')

/**
 * If within 30 days before or after account renewal date, returns banner data, otherwise empty object
 * @param {string} date ISO8601 date string
 * @param {string} [facilityStatus='ACTIVE'] Facility status one of ACTIVE, LOCKED, PENDING
 * @param {number} [invoicePastDue=null] Positive integer number or null
 * @returns {{ isAdmin: boolean, message: string, type: string }}
 */
export const subscriptionBannerData = (date, facilityStatus = 'ACTIVE', invoicePastDue = null) => {
  const contactText = i18n`Please contact your admin.`
  const invoiceExist = invoicePastDue && invoicePastDue > 0
  const dueDate = date ?? ((notProd && testDueDate) && testDueDate)
  if (!dueDate && !invoiceExist) return EMPTY_OBJECT
  const { daysDiff } = getDueDateDiff(dueDate)
  const type = daysDiff > 0 ? 'overdue' : 'warning'
  const absoluteDiff = invoiceExist && invoicePastDue > daysDiff ? invoicePastDue : Math.abs(daysDiff)
  const dayText = inflect('days', absoluteDiff)
  // More than 45 days before renewal is due, don't show banner
  if (absoluteDiff > 45 && type === 'warning' && !invoiceExist) return EMPTY_OBJECT
  let message = ''
  if (type === 'overdue' || invoiceExist) {
    if (invoiceExist && invoicePastDue > daysDiff) {
      message = [
        i18n`Friendly reminder, your account has an overdue invoice.`,
        contactText,
      ].join(' ')
    } else if (daysDiff < 30) {
      const suspendDate = formattedDate(getDateTime(dueDate).plus({ days: 30 }))
      message = [
        i18n`Friendly reminder, your subscription has expired and your account is ${absoluteDiff} ${dayText} overdue.`,
        i18n`Please contact your admin before ${suspendDate}.`,
      ].join(' ')
    } else {
      message = [
        facilityStatus === 'LOCKED'
          ? i18n`Your account is overdue and has been temporarily locked.`
          : i18n`Your account is ${daysDiff} days overdue.`,
        contactText,
      ].join(' ')
    }
  } else {
    message = [
      i18n`Friendly reminder, your subscription expires ${daysDiff === 0 ? i18n`today` : i18n`in ${absoluteDiff} ${dayText}`}.`,
      contactText
    ].join(' ')
  }
  return { message, type: invoiceExist ? 'overdue' : type }
}
